import { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import PageTransition from '@/components/PageTransition';
import NavigationProgress from '@/components/NavigationProgress';

// Eagerly load the homepage for fast initial render
import Index from '@/pages/Index';

// Lazy load all other pages to reduce initial bundle size
const CategoriesPage = lazy(() => import('@/pages/CategoriesPage'));
const CategoryPage = lazy(() => import('@/pages/CategoryPage'));
const ChecklistPage = lazy(() => import('@/pages/ChecklistPage'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const AuthPage = lazy(() => import('@/pages/AuthPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const SavedChecklistsPage = lazy(() => import('@/pages/SavedChecklistsPage'));
const SavedChecklistViewPage = lazy(() => import('@/pages/SavedChecklistViewPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ProfileSettingsPage = lazy(() => import('@/pages/ProfileSettingsPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@/pages/TermsOfServicePage'));
const AccessibilityPage = lazy(() => import('@/pages/AccessibilityPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const FAQPage = lazy(() => import('@/pages/FAQPage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const TeamManagementPage = lazy(() => import('@/pages/TeamManagementPage'));
const JoinTeamPage = lazy(() => import('@/pages/JoinTeamPage'));
const TeamChecklistViewPage = lazy(() => import('@/pages/TeamChecklistViewPage'));
const GenerateRedirectPage = lazy(() => import('@/pages/GenerateRedirectPage'));
const DeadlineCalendarPage = lazy(() => import('@/pages/DeadlineCalendarPage'));
const SOPsPage = lazy(() => import('@/pages/SOPsPage'));
const SOPEditorPage = lazy(() => import('@/pages/SOPEditorPage'));
const ExecutionModePage = lazy(() => import('@/pages/ExecutionModePage'));

const SharedChecklistPage = lazy(() => import('@/pages/SharedChecklistPage'));
const SubscriptionSuccessPage = lazy(() => import('@/pages/SubscriptionSuccessPage'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const SitemapRedirect = lazy(() => import('@/pages/SitemapRedirect'));
const SitemapPage = lazy(() => import('@/pages/SitemapPage'));
const LogoExportPage = lazy(() => import('@/pages/LogoExportPage'));

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <>
      <NavigationProgress />
      <AnimatePresence mode="wait">
        <Suspense fallback={<PageLoader />}>
          <Routes location={location} key={location.pathname}>
            <Route
              path="/"
              element={
                <PageTransition>
                  <Index />
                </PageTransition>
              }
            />
            <Route
              path="/categories"
              element={
                <PageTransition>
                  <CategoriesPage />
                </PageTransition>
              }
            />
            <Route
              path="/category/:category"
              element={
                <PageTransition>
                  <CategoryPage />
                </PageTransition>
              }
            />
            <Route
              path="/checklist/:id"
              element={
                <PageTransition>
                  <ChecklistPage />
                </PageTransition>
              }
            />
            <Route
              path="/search"
              element={
                <PageTransition>
                  <SearchPage />
                </PageTransition>
              }
            />
            <Route
              path="/auth"
              element={
                <PageTransition>
                  <AuthPage />
                </PageTransition>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PageTransition>
                  <ResetPasswordPage />
                </PageTransition>
              }
            />
            <Route
              path="/saved"
              element={
                <PageTransition>
                  <SavedChecklistsPage />
                </PageTransition>
              }
            />
            <Route
              path="/saved/:id"
              element={
                <PageTransition>
                  <SavedChecklistViewPage />
                </PageTransition>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PageTransition>
                  <DashboardPage />
                </PageTransition>
              }
            />
            <Route
              path="/profile"
              element={
                <PageTransition>
                  <ProfileSettingsPage />
                </PageTransition>
              }
            />
            <Route
              path="/admin"
              element={
                <PageTransition>
                  <AdminPage />
                </PageTransition>
              }
            />
            <Route
              path="/privacy"
              element={
                <PageTransition>
                  <PrivacyPolicyPage />
                </PageTransition>
              }
            />
            <Route
              path="/terms"
              element={
                <PageTransition>
                  <TermsOfServicePage />
                </PageTransition>
              }
            />
            <Route
              path="/accessibility"
              element={
                <PageTransition>
                  <AccessibilityPage />
                </PageTransition>
              }
            />
            <Route
              path="/contact"
              element={
                <PageTransition>
                  <ContactPage />
                </PageTransition>
              }
            />
            <Route
              path="/faq"
              element={
                <PageTransition>
                  <FAQPage />
                </PageTransition>
              }
            />
            <Route
              path="/pricing"
              element={
                <PageTransition>
                  <PricingPage />
                </PageTransition>
              }
            />
            <Route
              path="/team"
              element={
                <PageTransition>
                  <TeamManagementPage />
                </PageTransition>
              }
            />
            <Route
              path="/join-team"
              element={
                <PageTransition>
                  <JoinTeamPage />
                </PageTransition>
              }
            />
            <Route
              path="/team-checklist/:id"
              element={
                <PageTransition>
                  <TeamChecklistViewPage />
                </PageTransition>
              }
            />
            <Route
              path="/generate"
              element={
                <PageTransition>
                  <GenerateRedirectPage />
                </PageTransition>
              }
            />
            <Route
              path="/deadline-calendar"
              element={
                <PageTransition>
                  <DeadlineCalendarPage />
                </PageTransition>
              }
            />
            <Route
              path="/sops"
              element={
                <PageTransition>
                  <SOPsPage />
                </PageTransition>
              }
            />
            <Route
              path="/sop/:id"
              element={
                <PageTransition>
                  <SOPEditorPage />
                </PageTransition>
              }
            />
            <Route
              path="/execute/:type/:id"
              element={
                <PageTransition>
                  <ExecutionModePage />
                </PageTransition>
              }
            />
            <Route
              path="/shared/:token"
              element={
                <PageTransition>
                  <SharedChecklistPage />
                </PageTransition>
              }
            />
            <Route
              path="/subscription-success"
              element={
                <PageTransition>
                  <SubscriptionSuccessPage />
                </PageTransition>
              }
            />
            <Route path="/sitemap.xml" element={<SitemapRedirect />} />
            <Route
              path="/sitemap"
              element={
                <PageTransition>
                  <SitemapPage />
                </PageTransition>
              }
            />
            <Route
              path="/logo-export"
              element={
                <PageTransition>
                  <LogoExportPage />
                </PageTransition>
              }
            />
            <Route
              path="*"
              element={
                <PageTransition>
                  <NotFound />
                </PageTransition>
              }
            />
          </Routes>
        </Suspense>
      </AnimatePresence>
    </>
  );
};

export default AnimatedRoutes;
